import { cloneElement, useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Heading,
  Label,
  LabeledControl,
  Paragraph,
  Radio,
  Text,
  TextButton,
} from '@zillow/constellation-10';

import { getStringConfig } from '@zg-rentals/frontend-config/client';
import {
  useRentalManagerUserFreemiumWebApiHandler_details,
  useRentalManagerUserPropertyPaymentWebApiHandler_getDefaultPaymentMethod,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import { PAYMENT_METHOD, PAYMENT_PHASE } from '../../../constants/PaymentConstants';

import { EVENT_IDS as MODULAR_LISTING_EVENT_IDS } from '../../../analytics/events/modularListing';
import ListingActivationTerms from '../../ListingActivationTerms';

type Props = {
  creditCardCapture: React.ReactElement;
  isRenderedInPageView: boolean;
  onPaymentMethodChange: (method: (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD]) => void;
  setCurrentPhase: (phase: string) => void;
  isFormLoaded: boolean;
  listingAlias: string;
};

const SelectPaymentMethod = ({
  creditCardCapture,
  isRenderedInPageView = false,
  onPaymentMethodChange,
  setCurrentPhase,
  isFormLoaded,
  listingAlias,
}: Props) => {
  const creditCardCaptureRef = useRef<HTMLDivElement>(null);

  const {
    data: defaultPaymentMethod,
    isSuccess: defaultPaymentMethodSuccess,
    refetch: refetchDefaultPaymentMethod,
    isFetching: isDefaultPaymentMethodFetching,
  } = useRentalManagerUserPropertyPaymentWebApiHandler_getDefaultPaymentMethod({ listingAlias });

  const { data: freemiumDetailsData } = useRentalManagerUserFreemiumWebApiHandler_details(
    { listingAlias },
    { enabled: Boolean(listingAlias), staleTime: 10 * 1000 },
  );

  const iframeButtonText = getStringConfig('metroidIframeButtonText', 'Submit');

  const [paymentMethod, setPaymentMethod] = useState(
    defaultPaymentMethodSuccess ? PAYMENT_METHOD.DEFAULT : PAYMENT_METHOD.NEW,
  );

  useEffect(() => {
    if (defaultPaymentMethodSuccess && defaultPaymentMethod?.defaultPaymentMethodExists) {
      setPaymentMethod(PAYMENT_METHOD.DEFAULT);
    }
  }, [defaultPaymentMethodSuccess, defaultPaymentMethod]);

  const displaySelectPaymentMethod = Boolean(
    (defaultPaymentMethodSuccess === false && !isDefaultPaymentMethodFetching) ||
    (defaultPaymentMethodSuccess && defaultPaymentMethod?.defaultPaymentMethodExists),
  );

  const selectedProduct = freemiumDetailsData?.products?.find(
    (product) => product.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM,
  );

  useEffect(() => {
    const shouldPerformScrollEffect =
      creditCardCaptureRef.current && isFormLoaded && paymentMethod === PAYMENT_METHOD.NEW;
    let timerId: ReturnType<typeof setInterval> | null = null;

    if (shouldPerformScrollEffect) {
      // eslint-disable-next-line curly-quotes/no-straight-quotes
      const modalBodyQuery = '[data-c11n-component="Modal.Body"]';
      const ref: Window | Element | null = isRenderedInPageView ? window : document.querySelector(modalBodyQuery);
      const creditCardCaptureOffsetTop = creditCardCaptureRef.current.offsetTop - 40;

      if (ref) {
        timerId = setInterval(() => {
          const scrollHeight =
            ref instanceof Window ? ref.document.documentElement.scrollHeight : (ref as Element).scrollHeight;
          if (scrollHeight > 1000) {
            if (timerId) clearInterval(timerId);
            ref.scrollTo({
              top: creditCardCaptureOffsetTop,
              behavior: 'smooth',
            });
            timerId = null;
          }
        }, 500);
      }
    }
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [isFormLoaded, paymentMethod, isRenderedInPageView]);

  const { maskedNumber, cardBrand } = defaultPaymentMethod ?? {};
  const isDefaultPaymentMethodSelected = paymentMethod === PAYMENT_METHOD.DEFAULT;
  const isNewPaymentMethodSelected = paymentMethod === PAYMENT_METHOD.NEW;

  const renderPaymentMethods = () => (
    <>
      <Box
        css={{
          padding: '400',
          backgroundColor: isDefaultPaymentMethodSelected ? '#fafafa' : undefined,
        }}
        className="fs-mask"
      >
        <LabeledControl
          disabled={!defaultPaymentMethodSuccess}
          label={
            <Label>
              {defaultPaymentMethodSuccess ? (
                <Text css={{ letterSpacing: '0.05em' }}>
                  {maskedNumber}
                  <Text textStyle="body-bold" css={{ marginLeft: '400' }}>
                    {cardBrand}
                  </Text>
                </Text>
              ) : (
                'Default payment method'
              )}
            </Label>
          }
          control={
            <Radio
              data-test="default-payment-method-radio"
              name={PAYMENT_METHOD.DEFAULT}
              onChange={() => {
                setPaymentMethod(PAYMENT_METHOD.DEFAULT);
                onPaymentMethodChange(PAYMENT_METHOD.DEFAULT);
              }}
              checked={isDefaultPaymentMethodSelected}
            />
          }
        />
        {Boolean(!defaultPaymentMethodSuccess && !isDefaultPaymentMethodFetching) && (
          <Box css={{ paddingBlock: '400' }}>
            <Paragraph css={{ marginBottom: '400' }}>
              There was an error retrieving your default payment method.
            </Paragraph>
            <TextButton onClick={() => refetchDefaultPaymentMethod()}>Refresh</TextButton>
          </Box>
        )}
      </Box>
      <Box
        css={{
          padding: '400',
          backgroundColor: isNewPaymentMethodSelected ? '#fafafa' : undefined,
        }}
      >
        <LabeledControl
          label={<Label>Replace with new card</Label>}
          control={
            <Radio
              data-test="new-payment-method-radio"
              name={PAYMENT_METHOD.NEW}
              onChange={() => {
                setPaymentMethod(PAYMENT_METHOD.NEW);
                onPaymentMethodChange(PAYMENT_METHOD.NEW);
              }}
              checked={isNewPaymentMethodSelected}
            />
          }
        />
        {isNewPaymentMethodSelected &&
          cloneElement(creditCardCapture, {
            ref: creditCardCaptureRef,
            displayedInSelectPaymentMethodModal: true,
          })}
      </Box>
    </>
  );

  if (!displaySelectPaymentMethod) {
    const pricingContent =
      freemiumDetailsData?.isEnabled && selectedProduct ? (
        <Paragraph css={{ marginBlock: '400' }}>
          Your card will be charged a
          <strong data-type={selectedProduct?.type} data-test="display-price">
            {` one-time fee of ${selectedProduct?.price}. `}
          </strong>
          By purchasing this upgrade, you will have premium benefits for up to {selectedProduct?.duration}. Deactivate
          your listing when you find a renter.
        </Paragraph>
      ) : undefined;

    return cloneElement(creditCardCapture, { pricingContent });
  }

  return (
    <div data-testid="SelectPaymentMethod">
      <Heading level={1} textStyle="heading-sm">
        Checkout
      </Heading>
      {freemiumDetailsData?.isEnabled && selectedProduct && (
        <Paragraph css={{ marginBlock: '400' }}>
          Your card will be charged a
          <strong data-type={selectedProduct?.type} data-test="display-price">
            {` one-time fee of ${selectedProduct?.price}. `}
          </strong>
          By purchasing this upgrade, you will have premium benefits for up to {selectedProduct?.duration}. Deactivate
          your listing when you find a renter.
        </Paragraph>
      )}
      <Paragraph textStyle="body-lg-bold" css={{ paddingBottom: '400' }}>
        Payment method
      </Paragraph>
      {renderPaymentMethods()}
      {isDefaultPaymentMethodSelected && (
        <div data-testid="ModalActions">
          <ListingActivationTerms
            actionText={`By clicking ${iframeButtonText}`}
            isNotFlatFee={true}
            useFreemiumValues={true}
          />
          <Button
            data-testid="checkout-button"
            tone="brand"
            emphasis="filled"
            fluid
            css={{ paddingBlock: '400', marginTop: '200' }}
            onClick={() => setCurrentPhase(PAYMENT_PHASE.CREDIT_CARD_CHARGE)}
            data-eventid={MODULAR_LISTING_EVENT_IDS.COMPLETE_UPGRADE_TO_PREMIUM}
          >
            {iframeButtonText}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SelectPaymentMethod;
